/**
 * One repository and its sessions, in the coding rail.
 *
 * @module components/coding/sidebar/project-group
 */

import { ChevronRight, ExternalLink, Folder, MoreHorizontal, Plus } from "lucide-react"
import { useNavigate } from "react-router-dom"

import {
  hiddenCount,
  visibleSessions,
  type ProjectGroup as ProjectGroupData,
} from "@/components/coding/sidebar/group-sessions"
import { SessionRow } from "@/components/coding/sidebar/session-row"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
} from "@/components/ui/sidebar"
import { cn } from "@/lib/utils"

export interface ProjectGroupProps {
  group: ProjectGroupData
  basePath: string
  activeId: string | null
  collapsed: boolean
  onToggleCollapsed: () => void
  expanded: boolean
  onToggleExpanded: () => void
}

export function ProjectGroup({
  group,
  basePath,
  activeId,
  collapsed,
  onToggleCollapsed,
  expanded,
  onToggleExpanded,
}: ProjectGroupProps) {
  const navigate = useNavigate()
  const shown = visibleSessions(group, expanded)
  const hidden = hiddenCount(group)

  /** Starts a session already pointed at this repository. */
  const startHere = () => {
    navigate(`${basePath}?repo=${encodeURIComponent(group.key)}`)
  }

  return (
    <SidebarMenuItem>
      {/*
        The row is a button, not a link: a repository is not a place in this
        product — there is no page for one — so the only thing it can do is open
        and close. Making it a link would promise a destination that the tree
        cannot navigate to.
      */}
      <SidebarMenuButton
        onClick={onToggleCollapsed}
        className="group/project gap-2 font-medium"
        aria-expanded={!collapsed}
        data-testid={`coding-project-${group.key}`}
        title={group.key}
      >
        <ChevronRight
          className={cn(
            "size-3.5 shrink-0 text-muted-foreground transition-transform",
            !collapsed && "rotate-90",
          )}
        />
        <Folder className="size-4 shrink-0 text-muted-foreground" />
        <span className="truncate">{group.repoName}</span>

        {/* Count recedes on hover so the two actions can take its place rather
            than widening the row and truncating the name further. */}
        <span className="ml-auto flex shrink-0 items-center gap-0.5">
          <span className="type-sidebar-meta text-muted-foreground/70 group-hover/project:hidden">
            {group.sessions.length}
          </span>

          <span
            role="button"
            tabIndex={0}
            aria-label={`New session in ${group.repoName}`}
            data-testid={`coding-project-new-${group.key}`}
            className="hidden rounded p-0.5 hover:bg-sidebar-accent group-hover/project:block"
            onClick={(event) => {
              // The parent row toggles; without this a click on + would collapse
              // the project it is about to add a session to.
              event.stopPropagation()
              startHere()
            }}
            onKeyDown={(event) => {
              if (event.key !== "Enter" && event.key !== " ") return
              event.preventDefault()
              event.stopPropagation()
              startHere()
            }}
          >
            <Plus className="size-3.5" />
          </span>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <span
                role="button"
                tabIndex={0}
                aria-label={`${group.repoName} actions`}
                className="hidden rounded p-0.5 hover:bg-sidebar-accent group-hover/project:block"
                onClick={(event) => event.stopPropagation()}
              >
                <MoreHorizontal className="size-3.5" />
              </span>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem className="cursor-pointer" onClick={startHere}>
                <Plus className="mr-2 size-4" />
                New session here
              </DropdownMenuItem>
              <DropdownMenuItem
                className="cursor-pointer"
                onClick={() =>
                  window.open(
                    `https://github.com/${group.key}`,
                    "_blank",
                    "noopener,noreferrer",
                  )
                }
              >
                <ExternalLink className="mr-2 size-4" />
                Open on GitHub
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </span>
      </SidebarMenuButton>

      {!collapsed && (
        <SidebarMenuSub className="mr-0 pr-0">
          {shown.map((session) => (
            <SessionRow
              key={session.id}
              session={session}
              basePath={basePath}
              isActive={activeId === session.id}
            />
          ))}

          {/*
            Capped rather than scrolled. One busy repository would otherwise push
            every other project below the fold, and the tree exists to stop
            exactly that.
          */}
          {hidden > 0 && (
            <button
              type="button"
              onClick={onToggleExpanded}
              data-testid={`coding-project-more-${group.key}`}
              className="w-full px-2 py-1 text-left text-xs text-muted-foreground transition-colors hover:text-foreground"
            >
              {expanded ? "Show less" : `Show more (${hidden})`}
            </button>
          )}
        </SidebarMenuSub>
      )}
    </SidebarMenuItem>
  )
}

ProjectGroup.displayName = "ProjectGroup"
